import { Input, Select } from '@/shared/components';
import { PROPERTY_DOCUMENT_KIND_LABELS, type PropertyDocumentKind } from '@/shared/api';
import { FileBadge, RowStatusOrRemove } from './AkteUploadAffordances';
import {
  KIND_GROUPS,
  kindNeedsAmount,
  kindNeedsIssued,
  type FileUploadRow,
} from './filesUploadModel';

interface Props {
  row: FileUploadRow;
  onChange: (patch: Partial<FileUploadRow>) => void;
  onRemove: () => void;
  disabled: boolean;
}

export function FilesUploadRow({ row, onChange, onRemove, disabled }: Props) {
  const needsIssued = kindNeedsIssued(row.kind);
  const needsAmount = kindNeedsAmount(row.kind);
  return (
    <tr className="border-line/40 border-b align-top last:border-b-0">
      <td className="py-2 pr-3">
        <div className="flex items-start gap-2">
          <FileBadge file={row.file} />
          <div className="min-w-0">
            <div className="text-ink truncate text-xs font-medium">{row.file.name}</div>
            <div className="text-ink-faint mt-0.5 text-[10px]">
              {(row.file.size / 1024).toFixed(0)} KB
              {row.error && <span className="text-sealwax ml-2">{row.error}</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="py-2 pr-2">
        <Select
          value={row.kind}
          onChange={(e) => onChange({ kind: e.target.value as PropertyDocumentKind })}
          disabled={disabled}
          className="py-1.5! text-xs"
        >
          {KIND_GROUPS.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.kinds.map((k) => (
                <option key={k} value={k}>
                  {PROPERTY_DOCUMENT_KIND_LABELS[k]}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>
      </td>
      <td className="py-2 pr-2">
        <Input
          type="date"
          value={row.issuedOn}
          onChange={(e) => onChange({ issuedOn: e.target.value })}
          disabled={disabled}
          className="py-1.5! text-xs"
          aria-label={needsIssued ? 'Beleg-Datum (Pflicht)' : 'Beleg-Datum'}
        />
      </td>
      <td className="py-2 pr-2">
        {needsAmount ? (
          <div className="flex gap-1">
            <Input
              type="number"
              step="0.01"
              value={row.amount}
              onChange={(e) => onChange({ amount: e.target.value })}
              placeholder="0,00"
              disabled={disabled}
              className="py-1.5! text-xs"
            />
            <Select
              value={row.currency}
              onChange={(e) => onChange({ currency: e.target.value })}
              disabled={disabled}
              className="w-20! py-1.5! text-xs"
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="CHF">CHF</option>
            </Select>
          </div>
        ) : (
          <span className="text-ink-faint text-[10px]">—</span>
        )}
      </td>
      <td className="py-2 pr-2">
        <Input
          value={row.counterparty}
          onChange={(e) => onChange({ counterparty: e.target.value })}
          placeholder="Gegenpartei"
          disabled={disabled}
          className="py-1.5! text-xs"
        />
      </td>
      <td className="w-12 py-2 text-center">
        <RowStatusOrRemove status={row.status} onRemove={onRemove} disabled={disabled} />
      </td>
    </tr>
  );
}
